import { Button } from '@mui/material';
import axios from 'axios';
import classNames from 'classnames';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { DEFAULT_NS } from '@/i18n/types';

import TextField from '../../../components/Inputs/TextField';
import { LoadingPlaceholder } from '../../../components/LoadingPlaceholder';
import useMixpanel from '../../../hooks/useMixpanel';
import { useLoginService } from '../../../services/loginService/index';
import { getPortalRuntimeConfig } from '../../../services/runtimeConfig';
import rootStyles from '../index.module.scss';
import formStyles from '../LoginContent/index.module.scss';
import { LoginSuggestionsList } from '../LoginSuggestionsList';
import { getRedirectUriDetails } from '../redirect';
import { useLoginSuggestions } from '../useLoginSuggestions';
import { useRecentLoginValues } from '../useRecentLoginValues';
import styles from './index.module.scss';
import { SupportLink } from './SupportLink';

type UrlResponseItem = { provider: string; url: string };
type UrlsResponse = {
  loginUrls: UrlResponseItem[];
};

function LoginWithSsoForm() {
  const { state } = useLoginService();
  const { t } = useTranslation(DEFAULT_NS, { keyPrefix: 'auth.login' });
  const location = useLocation();
  const { cognito } = getPortalRuntimeConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { recentValues: recentEmails, saveValue: saveEmail } =
    useRecentLoginValues('auth.recentSsoEmails');
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<{ email: string }>({
    reValidateMode: 'onChange',
  });
  const { trackClick, trackFail, trackSuccess } = useMixpanel({
    initData: { component: 'sso_login' },
  });

  const emailRegistration = register('email', {
    required: 'Please enter email',
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email',
    },
  });

  const currentEmail = watch('email');
  const { suggestions, isOpen, selectSuggestion, onFocus, onBlur } = useLoginSuggestions({
    fieldName: 'email',
    currentValue: currentEmail,
    recentValues: recentEmails,
    setValue,
    onFieldBlur: emailRegistration.onBlur,
  });

  const onSubmit = async ({ email }: { email: string }) => {
    trackClick('sso_enter_email');

    setErrorMessage(null);
    setIsLoading(true);

    const { redirectUri, state: redirectState } = getRedirectUriDetails({
      redirectUri: `${window.location.origin}/login`,
      state:
        (location.state as { redirectTo?: string } | null)?.redirectTo ??
        new URLSearchParams(location.search).get('returnTo') ??
        undefined,
    });

    try {
      const resp = await axios.get<UrlsResponse>('/api/sso/urls', {
        params: {
          email,
          base_url: cognito.url,
          client_id: cognito.clientId,
          redirect_uri: redirectUri,
          response_type: 'token',
        },
      });
      setIsLoading(false);
      state.setLoginWithSso({ ...state.loginWithSso, value: true, state: resp.data });
      const url = resp.data.loginUrls[0]?.url;
      if (!url) {
        trackFail('sso_enter_email', { email, error: 'No SSO provider' });
        setErrorMessage('There is no Single Sign-On provider associated with this email.');
        return;
      }
      trackSuccess('sso_enter_email', { email });
      saveEmail(email);
      window.location.href = `${url}&state=${encodeURIComponent(redirectState)}`;
    } catch (e) {
      console.error(e);
      trackFail('sso_enter_email', { email, error: e });
      setErrorMessage('Something went wrong. Please try again later.');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    reset();
  }, [state.loginWithSso]);

  return (
    <div
      className={classNames(formStyles.root, rootStyles.boxContainer, 'w-full', {
        [rootStyles.hide]:
          !state.loginWithSso.value ||
          state.forgotPassword.value ||
          state.forgotPassword.state ||
          state.isWelcome,
      })}
    >
      <div className={styles.content}>
        <span className={rootStyles.title}>Sign in with your organization</span>
        {state.loginWithSso.isRequiredByOrganization ? (
          <p className="font-normal text-neutrals-n-400 text-center text-base leading-snug">
            {t('ssoRequired')}
          </p>
        ) : null}
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <LoginSuggestionsList
          id="sso-email-suggestions"
          open={isOpen}
          suggestions={suggestions}
          currentValue={currentEmail}
          onSelect={selectSuggestion}
        >
          <TextField
            placeholder="Company email"
            wrapperClassName={formStyles.inputWrapper}
            error={!!errors?.email}
            helperText={errors?.email?.message}
            inputClassName={formStyles.inputField}
            hideErrorIcon
            autoFocus={false}
            autoComplete={recentEmails.length ? 'off' : 'email'}
            variant="outlined"
            InputLabelProps={{
              classes: {
                shrink: formStyles.shrink,
              },
            }}
            inputProps={{
              role: 'combobox',
              'aria-autocomplete': 'list',
              'aria-expanded': isOpen,
              'aria-controls': 'sso-email-suggestions',
              'aria-haspopup': 'listbox',
            }}
            {...emailRegistration}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        </LoginSuggestionsList>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isValid || isLoading}
          className={clsx(formStyles.submitBtn, 'rounded-4')}
        >
          <LoadingPlaceholder size={20} isLoading={isLoading} className="justify-center flex">
            Continue
          </LoadingPlaceholder>
        </Button>
        {errorMessage ? <div className={formStyles.error}>{errorMessage}</div> : null}
      </form>
      <SupportLink />
    </div>
  );
}
export default observer(LoginWithSsoForm);
